<template>
  <div>
    <template v-if="lexisNexisChecklistsPending">
      <v-skeleton-loader type="table-thead, table-tbody"/>
    </template>
    <template v-else>
      <v-row v-if="lexisNexisChecklists.length" class="mb-2">
        <v-col>
          <div class="mb-5">
            {{ $t('project.checklist.dialogs.SaveChecklistFromTemplateDialog.LexisNexisModels.Description') }}
          </div>
          <div class="d-flex">
            <!-- Search for a checklist -->
            <SearchTextField v-model="search"
                             class="mb-6"
                             :tooltipText="$t('project.checklist.dialogs.SaveChecklistFromTemplateDialog.search')"
            />

            <!-- Dropdown menu for categories -->
            <p style="width: 200px">
              <AppSelect
                v-model="selectedCategory"
                :items="lexisNexisChecklistCategories"
                :label="$t('project.checklist.dialogs.SaveChecklistFromTemplateDialog.LexisNexisModels.allCategories')"
                class="ml-4"
                hide-details
                clearable
              /><!-- max-width: 200px is the same max-width as SearchTextField -->
            </p>
          </div>

          <v-row>
            <v-col md="7" sm="12">
              <v-data-table :headers="headers"
                            :items="tableItems"
                            :items-per-page="-1"
                            :mobile="false"
                            class="lexis-checklist-table"
                            style="border: 1px solid #EEEEEE;"
              >
                <template #body="{items, headers}">
                  <SavedChecklistTableBody :columns-count="headers.length"
                                           :items="items"
                                           isLexisNexisChecklist
                  />
                </template>
                <template #bottom></template>
              </v-data-table>
            </v-col>
            <v-col md="5" sm="12">
              <SavedChecklistPreview isLexisNexisChecklist/>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <div v-else class="d-flex flex-column justify-center align-center py-6">
        <template v-if="!lexisNexisChecklistsError">
          <app-icon-ds
            icon="tasks"
            size="sm"
          />
          <div class="text-center mt-8">
            {{ $t('project.checklist.dialogs.SaveChecklistFromTemplateDialog.LexisNexisModels.noData') }}
          </div>
        </template>
        <template v-else>
          {{ $t('project.checklist.dialogs.ImportChecklistDialog.getSavedChecklistsError') }}
        </template>
      </div>
    </template>
  </div>
</template>

<script>
  import { cloneDeep, debounce } from 'lodash-es'
  import { mapState, mapActions } from 'vuex'

  import AppSelect from '@/common/AppSelect.vue'
  import SearchTextField from '@/common/SearchTextField.vue'
  import { ISOToShortenedDate } from '@/common/utils/dates'
  import { toast } from '@/common/utils/toast'
  import i18n from '@/plugins/i18n'
  import SavedChecklistPreview from '@/project/checklist/dialogs/SavedChecklistPreview.vue'
  import SavedChecklistTableBody from '@/project/checklist/dialogs/SavedChecklistTableBody.vue'
  import { GET_LEXISNEXIS_CHECKLISTS } from '@/store/modules/checklist/action_types'
  import { SET_SELECTED_LEXISNEXIS_CHECKLIST } from '@/store/modules/checklist/mutation_types'

  export default {
    name: 'LexisNexisChecklistTableTemplate',
    components: {
      AppSelect,
      SearchTextField,
      SavedChecklistPreview,
      SavedChecklistTableBody,
    },
    data () {
      return {
        searchQuery: '',
        selectedCategory: '',
      }
    },
    computed: {
      ...mapState('checklist', [
        'lexisNexisChecklistsPending',
        'lexisNexisChecklists',
        'lexisNexisChecklistsError',
        'selectedLexisNexisChecklist',
      ]),
      search: {
        get () {
          return this.searchQuery
        },
        set: debounce(function (val) {
          this.searchQuery = val
        }, 1000),
      },
      lexisNexisChecklistCategories () {
        const categoriesObject = i18n.global.messages.value[this.$i18n.locale].project.checklist.dialogs.SaveChecklistFromTemplateDialog.LexisNexisModels.categories
        return Object.values(categoriesObject)
      },
      headers () {
        return [
          {
            key: 'name',
            title: this.$t('project.checklist.dialogs.SaveChecklistFromTemplateDialog.LexisNexisModels.name'),
            class: ['caption', 'font-weight-semi-bold'],
          },
          {
            key: 'category',
            sortable: false,
            title: this.$t('project.checklist.dialogs.SaveChecklistFromTemplateDialog.LexisNexisModels.category'),
            class: ['caption', 'font-weight-semi-bold'],
          },
          {
            key: 'author',
            sortable: false,
            title: this.$t('project.checklist.dialogs.SaveChecklistFromTemplateDialog.LexisNexisModels.author'),
          },
          {
            key: 'date',
            title: this.$t('project.checklist.dialogs.SaveChecklistFromTemplateDialog.LexisNexisModels.date'),
            class: ['caption', 'font-weight-semi-bold'],
            width: 150,
          },
        ]
      },
      tableItems () {
        let itemsToDisplay = this.searchQuery ? this.filteredSearchResult : this.lexisNexisChecklists

        if (this.selectedCategory) {
          let isSavedChecklistIncluded = false
          const selectedCategoryIndex = this.lexisNexisChecklistCategories.findIndex(i => i === this.selectedCategory)
          itemsToDisplay = itemsToDisplay.filter((item) => {
            const matchesItems = item.category === selectedCategoryIndex
            if (matchesItems && item?.name === this.selectedLexisNexisChecklist?.name) {
              isSavedChecklistIncluded = true
            }
            return matchesItems
          })

          if (!isSavedChecklistIncluded) {
            this.$store.commit(`checklist/${SET_SELECTED_LEXISNEXIS_CHECKLIST}`, null)
          }
        }
        return itemsToDisplay
      },
      filteredSearchResult () {
        if (!this.searchQuery) return []
        let checklists = cloneDeep(this.lexisNexisChecklists)
        const query = this.searchQuery.toLowerCase()
        let isSavedChecklistIncluded = false

        checklists = checklists.filter(checklist => {
          const matchesSearch = checklist.name.toLowerCase().includes(query) ||
                                checklist.author.toLowerCase().includes(query) ||
                                ISOToShortenedDate(checklist.date).toLowerCase().includes(query)

          if (matchesSearch && checklist?.name === this.selectedLexisNexisChecklist?.name) {
            isSavedChecklistIncluded = true
          }

          return matchesSearch
        })

        if (!isSavedChecklistIncluded) {
          this.$store.commit(`checklist/${SET_SELECTED_LEXISNEXIS_CHECKLIST}`, null)
        }

        return checklists
      },
    },
    created () {
      this.loadData()
    },
    methods: {
      ...mapActions('checklist', [GET_LEXISNEXIS_CHECKLISTS]),
      async loadData () {
        try {
          await this.GET_LEXISNEXIS_CHECKLISTS()
        } catch (e) {
          toast.error(this.$t('project.checklist.dialogs.ImportChecklistDialog.getSavedChecklistsError'))
        }
      },
    },
  }
</script>

<style scoped lang="scss">
  :deep(.lexis-checklist-table) {
    thead {
      background:#EEEEEE;
    }
  }
</style>
